import React, { useState } from 'react';
import axios from 'axios';
import PrivacySettings from './PrivacySettings';
import InviteFriend from './InviteFriend';
import LanguageSettings from './LanguageSettings';
import Help from './Help';
import PushNotificationSettings from './PushNotificationSettings';
import config from '../config';
import '../styles/Settings.css';

function Settings({ user, onBack, onUserUpdate }) {
  const [activeSection, setActiveSection] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountStatus, setDeleteAccountStatus] = useState('');

  const settingsSections = [
    {
      id: 'account',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      ),
      title: 'Account',
      description: 'Account settings'
    },
    {
      id: 'privacy',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <circle cx="12" cy="16" r="1"/>
          <path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
      ),
      title: 'Privacy',
      description: 'Block contacts, disappearing messages'
    },
    {
      id: 'avatar',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 1v6m0 6v6"/>
          <path d="m21 12-6-6m-6 6-6-6"/>
        </svg>
      ),
      title: 'Avatar',
      description: 'Create, edit, profile photo'
    },
    {
      id: 'chats',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
      ),
      title: 'Chats',
      description: 'Theme, wallpapers, chat history'
    },
    {
      id: 'notifications',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
      ),
      title: 'Notifications',
      description: 'Message, group & call tones'
    },
    {
      id: 'storage',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <ellipse cx="12" cy="5" rx="9" ry="3"/>
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
        </svg>
      ),
      title: 'Storage and data',
      description: 'Network usage, auto-download'
    },
    {
      id: 'app-language',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
        </svg>
      ),
      title: 'App language',
      description: 'English (device\'s language)'
    },
    {
      id: 'help',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      ),
      title: 'Help',
      description: 'Help center, contact us, privacy policy'
    },
    {
      id: 'invite',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M22 21v-2a4 4 0 00-3-3.87"/>
          <path d="M16 3.13a4 4 0 010 7.75"/>
        </svg>
      ),
      title: 'Invite a friend',
      description: 'Share UcmaApp with friends'
    }
  ];

  const handleSectionClick = (sectionId) => {
    if (sectionId === 'account') {
      setActiveSection('account');
    } else if (sectionId === 'privacy') {
      setActiveSection('privacy');
    } else if (sectionId === 'invite') {
      setActiveSection('invite');
    } else if (sectionId === 'app-language') {
      setActiveSection('app-language');
    } else if (sectionId === 'help') {
      setActiveSection('help');
    } else if (sectionId === 'notifications') {
      setActiveSection('notifications');
    } else {
      // TODO: Implement other sections
      alert(`${settingsSections.find(s => s.id === sectionId)?.title} settings coming soon!`);
    }
  };

  const handleBackToSettings = () => {
    setActiveSection(null);
  };

  const handleDeleteAccount = async () => {
    setDeleteAccountStatus('');

    if (!user?.id) {
      setDeleteAccountStatus('User info not found. Please login again.');
      return;
    }

    if (deleteConfirmText.trim() !== 'DELETE') {
      setDeleteAccountStatus('Please type DELETE exactly to continue.');
      return;
    }

    try {
      setIsDeletingAccount(true);
      await axios.delete(`${config.apiUrl}/api/auth/delete-account/${user.id}`);

      localStorage.removeItem('chatapp_user');
      localStorage.removeItem('callHistory');
      setDeleteAccountStatus('Your account has been deleted. Redirecting to login...');
      window.location.href = '/login';
    } catch (error) {
      console.error('Delete account error:', error);
      const msg = error?.response?.data?.error || 'Failed to delete account. Please try again.';
      setDeleteAccountStatus(msg);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // If a specific section is active, show that component
  if (activeSection === 'privacy') {
    return <PrivacySettings user={user} onBack={handleBackToSettings} onUserUpdate={onUserUpdate} />;
  }

  if (activeSection === 'invite') {
    return <InviteFriend onBack={handleBackToSettings} />;
  }

  if (activeSection === 'app-language') {
    return <LanguageSettings onBack={handleBackToSettings} />;
  }

  if (activeSection === 'help') {
    return <Help onBack={handleBackToSettings} user={user} />;
  }

  if (activeSection === 'notifications') {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button className="back-button" onClick={handleBackToSettings}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <h2>Notifications</h2>
        </div>
        <div className="settings-content">
          <div className="settings-detail-section">
            <PushNotificationSettings user={user} />
          </div>
        </div>
      </div>
    );
  }

  if (activeSection === 'account') {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button className="back-button" onClick={handleBackToSettings}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <h2>Account</h2>
        </div>
        <div className="settings-content">
          <div className="settings-detail-section">
            <div className="account-settings-card">
              <h3>Delete account</h3>
              <p>
                Your account and all related data will be permanently deleted.
              </p>
              <label className="delete-account-label" htmlFor="delete-account-confirm">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                id="delete-account-confirm"
                type="text"
                className="delete-account-input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="delete-account-btn"
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount || deleteConfirmText.trim() !== 'DELETE'}
              >
                {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
              </button>
              {deleteAccountStatus ? (
                <div className="delete-account-status">{deleteAccountStatus}</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main settings menu
  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="back-button" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5"/>
            <path d="M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h2>Settings</h2>
      </div>

      <div className="settings-content">
        {/* User Profile Section */}
        <div className="user-profile-section">
          <div className="user-profile-info">
            <div className="user-avatar-large">
              {user?.profileImage ? (
                <img 
                  src={`${config.apiUrl}${user.profileImage}`} 
                  alt={user.username}
                  className="user-profile-image-large"
                />
              ) : (
                <div className="user-avatar-placeholder-large">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
            </div>
            <div className="user-details">
              <div className="user-name-large notranslate">{user?.username || 'User'}</div>
              <div className="user-status-text notranslate">Hey there! I am using UcmaApp.</div>
            </div>
          </div>
          <button className="edit-profile-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        </div>

        {/* Settings Sections */}
        <div className="settings-sections">
          {settingsSections.map((section) => (
            <div
              key={section.id}
              className="settings-item"
              onClick={() => handleSectionClick(section.id)}
            >
              <div className="settings-item-icon">
                {section.icon}
              </div>
              <div className="settings-item-content">
                <div className="settings-item-title">{section.title}</div>
                {section.description ? (
                  <div className="settings-item-description">{section.description}</div>
                ) : null}
              </div>
              <div className="settings-item-arrow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Settings;
